<template>
  <button
    type="button"
    class="btn"
    :class="[
      'btn-' + severity,
      'btn-' + size,
      'text-' + size,
      { 'btn-icon-only': icon && !label },
      { 'btn-rounded': rounded },
      { 'btn-outlined': outlined },
      { 'btn-link': link },
      { 'btn-text': text },
      { 'btn-plain': plain },
      { 'btn-block': block }
    ]"
    :disabled="disabled || isLoading"
    :aria-label="ariaLabel"
    :aria-busy="isLoading"
    :aria-expanded="isDropdown ? 'false' : undefined"
    @click="onClick($event)"
  >
    <span v-if="isLoading">
      <Icon
        type="spinner-third"
        class="btn-icon fa-spin"
      />
    </span>
    <span
      v-else
      class="flex items-center"
      :class="[iconPos == 'right' ? 'flex-row-reverse' : '']"
    >
      <Icon
        v-if="icon"
        class="btn-icon"
        :class="['btn-icon-' + iconPos]"
        :type="icon"
      />
      <span>{{ label }}</span>
    </span>
    <Chip
      v-if="chipContent"
      :severity="chipSeverity"
      :label="chipContent"
      :size="size"
      class="ml-2"
    />
    <div
      v-if="isDropdown"
      class="btn-dropdown"
    >
      <Icon type="chevron-down" />
    </div>
  </button>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Icon from '@/components/icon/Icon.vue'
import Chip from '@/components/chip/Chip.vue'

const props = defineProps({
  severity: {
    type: String,
    default: 'primary'
  },
  label: {
    type: String,
    default: null
  },
  alt: {
    type: String,
    default: null
  },
  link: {
    type: Boolean,
    default: false
  },
  text: {
    type: Boolean,
    default: false
  },
  plain: {
    type: Boolean,
    default: false
  },
  outlined: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md'
  },
  rounded: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String,
    default: null
  },
  iconPos: {
    type: String,
    default: 'left'
  },
  block: {
    type: Boolean,
    default: false
  },
  inTable: {
    type: Boolean,
    default: false
  },
  isDropdown: {
    type: Boolean,
    default: false
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  routerLink: {
    type: String,
    default: null
  },
  url: {
    type: String,
    default: null
  },
  chipContent: {
    type: String,
    default: null
  },
  chipSeverity: {
    type: String,
    default: 'primary'
  }
})
const router = useRouter()

// Computed property for accessible label
const ariaLabel = computed(() => {
  // If there's an explicit alt prop, use it
  if (props.alt) {
    return props.alt
  }

  // If there's an explicit label, use it
  if (props.label) {
    return props.label
  }

  // For icon-only buttons, provide a meaningful label based on the icon
  if (props.icon && !props.label) {
    // Map common icon types to accessible labels
    const iconLabels = {
      user: 'User',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      trash: 'Delete',
      plus: 'Add',
      minus: 'Remove',
      search: 'Search',
      close: 'Close',
      times: 'Close',
      check: 'Confirm',
      'arrow-left': 'Previous',
      'arrow-right': 'Next',
      'chevron-down': 'Expand',
      'chevron-up': 'Collapse',
      download: 'Download',
      upload: 'Upload',
      print: 'Print',
      share: 'Share',
      heart: 'Favorite',
      star: 'Star',
      settings: 'Settings',
      cog: 'Settings',
      home: 'Home',
      menu: 'Menu',
      info: 'Information',
      warning: 'Warning',
      error: 'Error',
      success: 'Success'
    }

    return iconLabels[props.icon] || 'Button'
  }

  // Fallback for buttons without labels or icons
  return 'Button'
})

const onClick = event => {
  // Ripple Effect
  const button = event.currentTarget
  const ripple = document.createElement('span')
  const diameter = Math.max(button.clientWidth, button.clientHeight)
  const radius = diameter / 2

  ripple.style.width = ripple.style.height = `${diameter}px`
  ripple.style.left = `${event.clientX - button.getBoundingClientRect().left - radius}px`
  ripple.style.top = `${event.clientY - button.getBoundingClientRect().top - radius}px`
  ripple.classList.add('btn-ripple')

  button.appendChild(ripple)

  setTimeout(() => {
    ripple.remove()
  }, 1000)

  // If a url or routerLink is provided, navigate to that link
  if (props.url) {
    window.open(props.url, '_blank').focus()
  } else if (props.routerLink) {
    router.push(props.routerLink)
  }
}
</script>

<style>
.btn-ripple {
  position: absolute;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  transform: scale(0);
  animation: ripple-animation 0.6s linear;
  pointer-events: none;
}

@keyframes ripple-animation {
  to {
    transform: scale(2);
    opacity: 0;
  }
}

.btn {
  color: white;

  text-align: center;
  border: 1px solid transparent;
  border-radius: var(--rounded-md);
  font-weight: 600;

  position: relative;

  display: inline-flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  white-space: nowrap;
}

.btn-xs {
  padding: var(--s-05) var(--s-2);
  font-size: var(--text-xs);
  line-height: var(--text-xs-lh);
}
.btn-sm {
  padding: var(--s-1) var(--s-3);
  font-size: var(--text-sm);
  line-height: var(--text-sm-lh);
}
.btn-md {
  padding: var(--s-2) var(--s-4);
  font-size: var(--text-md);
  line-height: var(--text-md-lh);
}
.btn-lg {
  padding: var(--s-2) var(--s-5);
  font-size: var(--text-lg);
  line-height: var(--text-lg-lh);
}
.btn-xl {
  padding: var(--s-3) var(--s-7);
  font-size: var(--text-xl);
  line-height: var(--text-xl-lh);
}

.btn-rounded {
  border-radius: 100px;
}

.btn:not(.btn-icon) > .icon {
  margin-right: 5px;
}

.btn-icon.btn-rounded {
  border-radius: 100px;
}

.btn-primary {
  background-color: var(--bg-brand-solid);
}

.btn:hover {
  box-shadow: inset 0 0 100px 100px rgba(255, 255, 255, 0.1);
}
.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background-color: var(--bg-secondary-solid);
}

.btn-success {
  background-color: var(--bg-success-solid);
}

.btn-danger {
  background-color: var(--bg-error-solid);
}

.btn-warning {
  background-color: var(--bg-warning-solid);
}

.btn-info {
  background-color: var(--bg-info-solid);
}

.btn-dropdown {
  margin-left: var(--s-2);
}

/* LINK */
.btn-link {
  background-color: transparent;
  color: var(--text-brand);
}
.btn-link:hover {
  text-decoration: underline;
}

/* TEXT */
.btn-text {
  background-color: transparent;
  color: var(--text-secondary);
  padding: 0;
}
.btn-text:hover {
  background-color: transparent;
}
.btn-text.btn-primary {
  color: var(--text-brand);
}
.btn-text.btn-secondary {
  color: var(--text-secondary);
}
.btn-text.btn-success {
  color: var(--text-success);
}
.btn-text.btn-warning {
  color: var(--text-warning);
}
.btn-text.btn-danger {
  color: var(--text-error);
}
.btn-text.btn-info {
  color: var(--text-info);
}

/* PLAIN */
.btn-plain {
  background-color: transparent;
  border-color: transparent;
}
.btn-plain.btn-primary {
  color: var(--bg-brand-solid);
}
.btn-plain.btn-primary:enabled:hover {
  background-color: var(--bg-brand-light);
}
.btn-plain.btn-secondary,
.btn-plain {
  color: var(--text-tertiary);
}
.btn-plain.btn-secondary:enabled:hover {
  background-color: var(--bg-tertiary);
}
.btn-plain.btn-success {
  color: var(--text-success);
}
.btn-plain.btn-success:enabled:hover {
  background-color: var(--bg-success-light);
}
.btn-plain.btn-warning {
  color: var(--text-warning);
}
.btn-plain.btn-warning:enabled:hover {
  background-color: var(--bg-warning-light);
}
.btn-plain.btn-danger {
  color: var(--text-error);
}
.btn-plain.btn-danger:enabled:hover {
  background-color: var(--bg-error-light);
}
.btn-plain.btn-info {
  color: var(--text-info);
}
.btn-plain.btn-info:enabled:hover {
  background-color: var(--bg-info-light);
}

/* OUTLINED */
.btn-outlined {
  background-color: transparent;
  border: 1px solid;
}
.btn-outlined.btn-primary {
  color: var(--fg-brand);
  border-color: var(--border-brand);
}
.btn-outlined.btn-primary:enabled:hover {
  background-color: var(--bg-brand-light);
}
.btn-outlined.btn-secondary {
  color: var(--text-secondary);
  border-color: var(--border-primary);
}
.btn-outlined.btn-secondary:enabled:hover {
  background-color: var(--bg-secondary-light);
}
.btn-outlined.btn-success {
  color: var(--text-success);
}
.btn-outlined.btn-success:enabled:hover {
  background-color: var(--bg-success-light);
}
.btn-outlined.btn-warning {
  color: var(--text-warning);
}
.btn-outlined.btn-warning:enabled:hover {
  background-color: var(--bg-warning-light);
}
.btn-outlined.btn-danger {
  color: var(--text-error);
}
.btn-outlined.btn-danger:enabled:hover {
  background-color: var(--bg-error-light);
}
.btn-outlined.btn-info {
  color: var(--text-info);
}
.btn-outlined.btn-info:enabled:hover {
  background-color: var(--bg-info-light);
}

/* ROUNDED */
.btn-rounded {
  border-radius: 100px;
}

/* ICON  */
.btn-icon-only {
  /* padding-left: 0;
  padding-right: 0; */
  /* width: 2.5rem; */
  flex-shrink: 0;
}
.btn-icon-only.btn-xs {
  width: 1.6rem;
}
.btn-icon-only.btn-sm {
  width: 2.4rem;
}
.btn-icon-only.btn-md {
  width: 2.7rem;
}
.btn-icon-only.btn-lg {
  width: 2.6rem;
}
.btn-icon-only.btn-xl {
  width: 3.1rem;
}

.btn-icon-left {
  margin-right: var(--s-2);
}
.btn-icon-right {
  margin-left: var(--s-2);
}

.btn-icon-only .btn-icon {
  margin: 0;
}

/* BLOCK */
.btn-block {
  width: 100%;
}
</style>
